import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FCC } from '@eluve/components';

import { OverwriteExistingSummaryDialog } from './OverwriteExistingSummaryDialog';

interface AppointmentDialogContextValue {
  openOverwriteSummaryDialog: (appointmentId: string) => void;
  closeOverwriteSummaryDialog: () => void;
}

const AppointmentDialogContext = createContext<
  AppointmentDialogContextValue | undefined
>(undefined);

export const useAppointmentDialogs = () => {
  const context = useContext(AppointmentDialogContext);
  if (!context) {
    throw new Error(
      'useAppointmentDialogs must be used within AppointmentDialogProvider',
    );
  }
  return context;
};

export const AppointmentDialogProvider: FCC = ({ children }) => {
  const [overwriteSummaryOpen, setOverwriteSummaryOpen] = useState(false);
  const [activeAppointmentId, setActiveAppointmentId] = useState<string>();

  const openOverwriteSummaryDialog = useCallback((appointmentId: string) => {
    setActiveAppointmentId(appointmentId);
    setOverwriteSummaryOpen(true);
  }, []);

  const closeOverwriteSummaryDialog = useCallback(() => {
    setOverwriteSummaryOpen(false);
    setActiveAppointmentId(undefined);
  }, []);

  const value = useMemo(
    () => ({
      openOverwriteSummaryDialog,
      closeOverwriteSummaryDialog,
    }),
    [openOverwriteSummaryDialog, closeOverwriteSummaryDialog],
  );

  return (
    <AppointmentDialogContext.Provider value={value}>
      {children}
      <OverwriteExistingSummaryDialog
        isOpen={overwriteSummaryOpen}
        appointmentId={activeAppointmentId}
      />
    </AppointmentDialogContext.Provider>
  );
};
